import HighNoise from "@audio/high_short.mp3";
import LowNoise from "@audio/low_short.mp3";
import { Trans } from "@lingui/macro";
import { Box, Modal, Stack, Typography } from "@mui/material";
import { HCButton } from "@root/components/atoms/HCButton/Component";
import { DoorgaanIcon } from "@root/icons/Icons";
import { modalStyle } from "@root/misc/constants";
import { hasValue } from "@root/misc/helpers";
import { Tone } from "@root/misc/types";
import { cardStyle } from "@root/utils/ui";
import React from "react";
import { useLocalStorage } from "usehooks-ts";

const possibleKeys = [
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "a",
  "b",
  "c",
  "d",
  "e",
  "1",
  "2",
  "3",
  "4",
  "5",
  "Enter",
  "Backspace",
  "y",
  "n",
  "/",
  "ArrowUp",
  "ArrowRight",
  "ArrowDown",
  "ArrowLeft",
];

export function KeyboardPreview(): JSX.Element {
  const [isLeftPedalF8] = useLocalStorage<string | null>("isLeftPedalF8", null);
  const [pressedKey, setPressedKey] = React.useState<string | null>();
  const [cueOpen, setCueOpen] = React.useState<boolean>(false);
  const modalCueBtnRef = React.useRef<HTMLButtonElement>(null);

  const handleUserKeyPress = React.useCallback((event: KeyboardEvent): void => {
    event.preventDefault();

    if (event.repeat) {
      return;
    }

    if (possibleKeys.includes(event.key)) {
      const { key } = event;
      const lowerKey = key.toLowerCase();
      setPressedKey(lowerKey);
    }
  }, []);

  const handleUserKeyRelease = React.useCallback((event: KeyboardEvent): void => {
    if (event.repeat) {
      return;
    }

    if (possibleKeys.includes(event.key)) {
      event.preventDefault();
      const { key } = event;
      const lowerKey = key.toLowerCase();
      setPressedKey((old) => (lowerKey === old ? null : old));
    }
  }, []);

  const [currentTone, setCurrentTone] = React.useState<Tone | null>(null);

  const playAudio = (tone: Tone | null): void => {
    setCurrentTone((old) => {
      return old === tone ? null : tone;
    });
  };

  const returnTone = (): string => {
    if (currentTone === Tone.high) {
      return HighNoise;
    }
    return LowNoise;
  };
  React.useEffect(() => {
    playAudio(null);

    if (pressedKey === "f6") {
      playAudio(Tone.low);
    }
    if (pressedKey === "f7") {
      playAudio(Tone.high);
    }
  }, [pressedKey]);

  React.useEffect(() => {
    window.addEventListener("keydown", handleUserKeyPress);
    window.addEventListener("keyup", handleUserKeyRelease);
    return () => {
      window.removeEventListener("keydown", handleUserKeyPress);
      window.removeEventListener("keyup", handleUserKeyRelease);
    };
  }, [handleUserKeyPress, handleUserKeyRelease]);

  React.useEffect(() => {
    handleCueOpen();
  }, []);

  // This handles the modal opening and focussing on the button
  const handleCueOpen = (): void => {
    setCueOpen(true);
    setTimeout(() => {
      modalCueBtnRef.current?.focus();
    }, 100);
  };
  const handleCueClose = (): void => {
    setCueOpen(false);
  };

  const endFn = (): void => {};

  const leftPedal = isLeftPedalF8 === "true" ? "f8" : "f9";
  const rightPedal = isLeftPedalF8 === "true" ? "f9" : "f8";

  return (
    <>
      {hasValue(currentTone) && (
        <audio src={returnTone()} loop autoPlay onEnded={() => setCurrentTone(null)} />
      )}
      <Modal
        disableAutoFocus
        keepMounted
        open={cueOpen}
        onClose={handleCueClose}
        aria-labelledby="modal-cue-title"
        aria-describedby="modal-cue-description"
      >
        <Box sx={modalStyle}>
          <Stack
            direction="column"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ height: "100%", ...cardStyle }}
          >
            <div>
              <Typography id="modal-cue-title" variant="h6" component="h2">
                <Trans id="introduction.modal.title">Welkom</Trans>
              </Typography>
              <Typography id="modal-cue-description" sx={{ mt: 2 }}>
                <Trans id="introduction.modal.description">
                  Dit is een introductie waarbij je uitleg krijgt over het speciale toetsenbord. Als
                  je dit venster sluit door middel van de knop 'Doorgaan', dan kun je daarna het
                  toetsenbord uitproberen. <br />
                  <br />
                  Druk gerust allerlei knoppen in. Als je klaar bent, druk dan op de knop
                  'BACKSPACE' of gebruik de muis om op de knop 'Introductie afsluiten' te klikken.
                </Trans>
              </Typography>
            </div>
            <HCButton
              state={{ kind: "primary" }}
              ref={modalCueBtnRef}
              variant="contained"
              endIcon={<DoorgaanIcon />}
              onClick={() => handleCueClose()}
            >
              <Trans id="introduction.modal.button">Doorgaan</Trans>
            </HCButton>
          </Stack>
        </Box>
      </Modal>

      <div style={{ padding: "8px" }}>
        <svg
          width="100%"
          viewBox="0 0 1150 438"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ borderRadius: "16px" }}
        >
          <g clip-path="url(#clip0_2540_19046)">
            <rect width="1150" height="438" rx="10" fill="#D9D9D9" />
            <rect width="1150" height="438" fill="var(--body-background-color)" />
            <rect x="694" y="46" width="252" height="102" rx="6" fill="#818181" />
            <rect x="694" y="168" width="152" height="52" rx="6" fill="#818181" />
            <path
              d="M737.667 289.667C740.98 289.667 743.667 286.98 743.667 283.667V246C743.667 242.686 746.353 240 749.667 240H790.333C793.647 240 796.333 242.686 796.333 246V283.667C796.333 286.98 799.02 289.667 802.333 289.667H840C843.314 289.667 846 292.353 846 295.667V336.211C846 339.572 843.24 342.278 839.879 342.21L802.454 341.457C799.094 341.389 796.333 344.094 796.333 347.455V386C796.333 389.314 793.647 392 790.333 392H749.667C746.353 392 743.667 389.314 743.667 386V348.333C743.667 345.02 740.98 342.333 737.667 342.333H700C696.686 342.333 694 339.647 694 336.333V295.667C694 292.353 696.686 289.667 700 289.667H737.667Z"
              fill="#818181"
            />

            <g id="1">
              <rect
                x="695"
                y="47"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "1" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M713.873 83.0004C713.473 83.0004 713.273 82.7004 713.273 82.1004V81.7404C713.273 81.1404 713.473 80.8404 713.873 80.8404H719.093V64.6704L713.783 65.5704C713.643 65.5704 713.523 65.4804 713.423 65.3004C713.343 65.1204 713.283 64.9104 713.243 64.6704C713.203 64.4304 713.183 64.2204 713.183 64.0404C713.183 63.7404 713.363 63.5504 713.723 63.4704L720.683 61.9404C720.903 61.9404 721.113 62.0004 721.313 62.1204C721.513 62.2404 721.613 62.3904 721.613 62.5704V80.8404H726.053C726.453 80.8404 726.653 81.1404 726.653 81.7404V82.1004C726.653 82.7004 726.453 83.0004 726.053 83.0004H713.873Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="695"
                y="47"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "1" ? "white" : "#818181"}
                stroke-width={pressedKey === "1" ? 1 : 2}
              />
            </g>

            <g id="a">
              <rect
                x="695"
                y="97"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "a" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M711.951 133.06C711.091 133.06 710.661 132.89 710.661 132.55C710.661 132.49 710.671 132.43 710.691 132.37C710.711 132.29 710.731 132.21 710.751 132.13L717.861 112.42C717.961 112.1 718.361 111.94 719.061 111.94H719.931C720.631 111.94 721.031 112.1 721.131 112.42L728.241 132.16C728.301 132.3 728.331 132.43 728.331 132.55C728.331 132.89 727.901 133.06 727.041 133.06H726.801C726.501 133.06 726.241 133.03 726.021 132.97C725.801 132.89 725.661 132.76 725.601 132.58L723.831 127.66H715.011L713.301 132.58C713.241 132.76 713.101 132.89 712.881 132.97C712.661 133.03 712.401 133.06 712.101 133.06H711.951ZM715.851 125.38H723.021L719.871 116.44C719.791 116.16 719.721 115.87 719.661 115.57C719.601 115.27 719.551 114.98 719.511 114.7H719.451C719.391 114.98 719.321 115.27 719.241 115.57C719.181 115.87 719.101 116.16 719.001 116.44L715.851 125.38Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="695"
                y="97"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "a" ? "white" : "#818181"}
                stroke-width={pressedKey === "a" ? 1 : 2}
              />
            </g>

            <g id="y">
              <rect
                x="695"
                y="169"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "y" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M713.488 199.09C713.348 199.09 713.188 199.085 713.008 199.075C712.838 199.065 712.673 199.04 712.513 199C712.353 198.97 712.223 198.925 712.123 198.865C712.023 198.795 711.973 198.71 711.973 198.61C711.973 198.59 711.983 198.515 712.003 198.385C712.023 198.245 712.058 198.11 712.108 197.98C712.158 197.85 712.223 197.785 712.303 197.785C712.363 197.785 712.473 197.81 712.633 197.86C712.803 197.9 713.008 197.92 713.248 197.92C713.988 197.92 714.523 197.775 714.853 197.485C715.183 197.185 715.348 196.715 715.348 196.075V188.8C715.348 188.58 715.548 188.47 715.948 188.47H716.068C716.468 188.47 716.668 188.58 716.668 188.8V195.925C716.668 196.555 716.563 197.11 716.353 197.59C716.143 198.06 715.803 198.43 715.333 198.7C714.873 198.96 714.258 199.09 713.488 199.09Z"
                fill="black"
                fill-opacity="0.83"
              />
              <path
                d="M718.743 199.03C718.313 199.03 718.098 198.945 718.098 198.775C718.098 198.745 718.103 198.715 718.113 198.685C718.123 198.645 718.133 198.605 718.143 198.565L721.698 188.71C721.748 188.55 721.948 188.47 722.298 188.47H722.733C723.083 188.47 723.283 188.55 723.333 188.71L726.888 198.58C726.918 198.65 726.933 198.715 726.933 198.775C726.933 198.945 726.718 199.03 726.288 199.03H726.168C726.018 199.03 725.888 199.015 725.778 198.985C725.668 198.945 725.598 198.88 725.568 198.79L724.683 196.33H720.273L719.418 198.79C719.388 198.88 719.318 198.945 719.208 198.985C719.098 199.015 718.968 199.03 718.818 199.03H718.743ZM720.693 195.19H724.278L722.703 190.72C722.663 190.58 722.628 190.435 722.598 190.285C722.568 190.135 722.543 189.99 722.523 189.85H722.493C722.463 189.99 722.428 190.135 722.388 190.285C722.358 190.435 722.318 190.58 722.268 190.72L720.693 195.19Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="695"
                y="169"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "y" ? "white" : "#818181"}
                stroke-width={pressedKey === "y" ? 1 : 2}
              />
            </g>

            <g id="arrowleft">
              <rect
                x="695"
                y="291"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "arrowleft" ? "#ababab" : "#D8D8D8"}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M716.931 319.283C716.931 320.049 716.104 320.531 715.438 320.153L710.533 317.37C709.858 316.987 709.858 316.013 710.533 315.63L715.438 312.847C716.104 312.469 716.931 312.951 716.931 313.717V314.721C716.931 315.273 717.379 315.721 717.931 315.721H731.207C731.645 315.721 732 316.07 732 316.5C732 316.93 731.645 317.279 731.207 317.279H717.931C717.379 317.279 716.931 317.727 716.931 318.279V319.283Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="695"
                y="291"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "arrowleft" ? "white" : "#818181"}
                stroke-width={pressedKey === "arrowleft" ? 1 : 2}
              />
            </g>

            <g id="3">
              <rect
                x="795"
                y="47"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "3" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M818.853 83.1803C817.973 83.1803 817.163 83.1203 816.423 83.0003C815.703 82.9003 815.073 82.7703 814.533 82.6103C813.993 82.4303 813.573 82.2403 813.273 82.0403C812.973 81.8403 812.823 81.6503 812.823 81.4703C812.823 81.2703 812.883 81.0603 813.003 80.8403C813.143 80.6003 813.293 80.4003 813.453 80.2403C813.633 80.0603 813.773 79.9703 813.873 79.9703C814.073 79.9703 814.373 80.0603 814.773 80.2403C815.173 80.4003 815.693 80.5703 816.333 80.7503C816.973 80.9303 817.733 81.0203 818.613 81.0203C819.213 81.0203 819.843 80.9003 820.503 80.6603C821.183 80.4203 821.753 80.0003 822.213 79.4003C822.693 78.8003 822.933 77.9603 822.933 76.8803C822.933 75.3403 822.523 74.3003 821.703 73.7603C820.883 73.2003 819.793 72.9203 818.433 72.9203H817.443C817.043 72.9203 816.843 72.6203 816.843 72.0203V71.6903C816.843 71.0903 817.043 70.7903 817.443 70.7903H818.313C819.353 70.7903 820.153 70.5603 820.713 70.1003C821.293 69.6403 821.703 69.1103 821.943 68.5103C822.183 67.8903 822.303 67.3403 822.303 66.8603C822.303 66.4803 822.203 66.0703 822.003 65.6303C821.823 65.1703 821.473 64.7803 820.953 64.4603C820.453 64.1403 819.713 63.9803 818.733 63.9803C817.833 63.9803 817.093 64.0603 816.513 64.2203C815.933 64.3803 815.483 64.5503 815.163 64.7303C814.843 64.8903 814.593 64.9703 814.413 64.9703C814.313 64.9703 814.183 64.8703 814.023 64.6703C813.863 64.4703 813.723 64.2503 813.603 64.0103C813.483 63.7503 813.423 63.5503 813.423 63.4103C813.423 63.1903 813.613 62.9803 813.993 62.7803C814.373 62.5803 814.843 62.4103 815.403 62.2703C815.983 62.1303 816.573 62.0203 817.173 61.9403C817.793 61.8603 818.333 61.8203 818.793 61.8203C820.573 61.8203 822.023 62.2603 823.143 63.1403C824.283 64.0203 824.853 65.3503 824.853 67.1303C824.853 68.1703 824.533 69.0403 823.893 69.7403C823.273 70.4403 822.563 70.9503 821.763 71.2703V71.3903C822.363 71.5503 822.953 71.8703 823.533 72.3503C824.113 72.8103 824.593 73.4203 824.973 74.1803C825.353 74.9403 825.543 75.8503 825.543 76.9103C825.543 78.0703 825.333 79.0503 824.913 79.8503C824.513 80.6503 823.973 81.3003 823.293 81.8003C822.633 82.2803 821.913 82.6303 821.133 82.8503C820.353 83.0703 819.593 83.1803 818.853 83.1803Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="795"
                y="47"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "3" ? "white" : "#818181"}
                stroke-width={pressedKey === "3" ? 1 : 2}
              />
            </g>

            <g id="c">
              <rect
                x="795"
                y="97"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "c" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M821.583 133.18C818.303 133.18 815.873 132.32 814.293 130.6C812.713 128.86 811.923 126.16 811.923 122.5C811.923 115.38 815.163 111.82 821.643 111.82C822.323 111.82 822.983 111.87 823.623 111.97C824.263 112.07 824.843 112.2 825.363 112.36C825.883 112.52 826.313 112.7 826.653 112.9C827.133 113.18 827.373 113.44 827.373 113.68C827.373 113.84 827.313 114.06 827.193 114.34C827.073 114.62 826.933 114.87 826.773 115.09C826.633 115.31 826.493 115.42 826.353 115.42C826.253 115.42 826.013 115.31 825.633 115.09C825.273 114.87 824.763 114.65 824.103 114.43C823.463 114.21 822.663 114.1 821.703 114.1C819.303 114.1 817.523 114.77 816.363 116.11C815.223 117.43 814.653 119.57 814.653 122.53C814.653 125.41 815.213 127.53 816.333 128.89C817.473 130.23 819.223 130.9 821.583 130.9C822.623 130.9 823.493 130.78 824.193 130.54C824.913 130.28 825.483 130.03 825.903 129.79C826.343 129.53 826.623 129.4 826.743 129.4C826.883 129.4 827.033 129.51 827.193 129.73C827.353 129.95 827.493 130.19 827.613 130.45C827.733 130.71 827.793 130.9 827.793 131.02C827.793 131.16 827.703 131.32 827.523 131.5C827.363 131.68 827.133 131.85 826.833 132.01C826.473 132.21 826.023 132.4 825.483 132.58C824.963 132.76 824.373 132.9 823.713 133C823.053 133.12 822.343 133.18 821.583 133.18Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="795"
                y="97"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "c" ? "white" : "#818181"}
                stroke-width={pressedKey === "c" ? 1 : 2}
              />
            </g>

            <g id="n">
              <rect
                x="795"
                y="169"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "n" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M807.871 199.03C807.471 199.03 807.271 198.92 807.271 198.7V188.8C807.271 188.58 807.471 188.47 807.871 188.47H808.186C808.356 188.47 808.486 188.49 808.576 188.53C808.666 188.57 808.746 188.645 808.816 188.755L813.526 195.865C813.626 196.015 813.716 196.165 813.796 196.315C813.876 196.455 813.951 196.605 814.021 196.765L814.081 196.75C814.071 196.58 814.061 196.425 814.051 196.285C814.051 196.135 814.051 195.95 814.051 195.73V188.8C814.051 188.58 814.251 188.47 814.651 188.47H814.756C815.156 188.47 815.356 188.58 815.356 188.8V198.7C815.356 198.92 815.156 199.03 814.756 199.03H814.456C814.316 199.03 814.206 199.015 814.126 198.985C814.046 198.945 813.981 198.89 813.931 198.82L809.101 191.53C809.001 191.38 808.911 191.235 808.831 191.095C808.761 190.945 808.686 190.79 808.606 190.63L808.546 190.645C808.566 190.815 808.576 190.975 808.576 191.125C808.576 191.265 808.576 191.445 808.576 191.665V198.7C808.576 198.92 808.376 199.03 807.976 199.03H807.871Z"
                fill="black"
                fill-opacity="0.83"
              />
              <path
                d="M818.622 199C818.322 199 818.172 198.85 818.172 198.55V188.95C818.172 188.65 818.322 188.5 818.622 188.5H823.857C824.057 188.5 824.157 188.65 824.157 188.95V189.19C824.157 189.49 824.057 189.64 823.857 189.64H819.492V193.06H823.407C823.607 193.06 823.707 193.21 823.707 193.51V193.75C823.707 194.05 823.607 194.2 823.407 194.2H819.492V197.86H823.872C824.072 197.86 824.172 198.01 824.172 198.31V198.55C824.172 198.85 824.072 199 823.872 199H818.622Z"
                fill="black"
                fill-opacity="0.83"
              />
              <path
                d="M826.811 199C826.511 199 826.361 198.85 826.361 198.55V188.95C826.361 188.65 826.511 188.5 826.811 188.5H832.046C832.246 188.5 832.346 188.65 832.346 188.95V189.19C832.346 189.49 832.246 189.64 832.046 189.64H827.681V193.06H831.596C831.796 193.06 831.896 193.21 831.896 193.51V193.75C831.896 194.05 831.796 194.2 831.596 194.2H827.681V197.86H832.061C832.261 197.86 832.361 198.01 832.361 198.31V198.55C832.361 198.85 832.261 199 832.061 199H826.811Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="795"
                y="169"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "n" ? "white" : "#818181"}
                stroke-width={pressedKey === "n" ? 1 : 2}
              />
            </g>

            <g id="arrowright">
              <rect
                x="795"
                y="291"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "arrowright" ? "#ababab" : "#D8D8D8"}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M824.069 313.717C824.069 312.951 824.896 312.469 825.562 312.847L830.467 315.63C831.142 316.013 831.142 316.987 830.467 317.37L825.562 320.153C824.896 320.531 824.069 320.049 824.069 319.283L824.069 318.279C824.069 317.727 823.621 317.279 823.069 317.279L809.793 317.279C809.355 317.279 809 316.93 809 316.5C809 316.07 809.355 315.721 809.793 315.721L823.069 315.721C823.621 315.721 824.069 315.273 824.069 314.721L824.069 313.717Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="795"
                y="291"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "arrowright" ? "white" : "#818181"}
                stroke-width={pressedKey === "arrowright" ? 1 : 2}
              />
            </g>

            <g id="2">
              <rect
                x="745"
                y="47"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "2" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M763.603 83.0003C763.483 83.0003 763.373 82.9303 763.273 82.7903C763.193 82.6503 763.123 82.4803 763.063 82.2803C763.023 82.0603 763.003 81.8603 763.003 81.6803C763.003 81.4203 763.043 81.2503 763.123 81.1703L770.863 71.4503C771.503 70.6103 771.963 69.9503 772.243 69.4703C772.523 68.9903 772.703 68.5803 772.783 68.2403C772.863 67.9003 772.903 67.5203 772.903 67.1003C772.903 66.1403 772.563 65.3803 771.883 64.8203C771.223 64.2603 770.313 63.9803 769.153 63.9803C768.253 63.9803 767.493 64.1103 766.873 64.3703C766.273 64.6303 765.793 64.8903 765.433 65.1503C765.093 65.3903 764.833 65.5103 764.653 65.5103C764.553 65.5103 764.423 65.4203 764.263 65.2403C764.123 65.0403 763.993 64.8203 763.873 64.5803C763.753 64.3403 763.693 64.1503 763.693 64.0103C763.693 63.6903 763.883 63.4003 764.263 63.1403C764.643 62.8803 765.123 62.6503 765.703 62.4503C766.283 62.2503 766.873 62.1003 767.473 62.0003C768.093 61.8803 768.633 61.8203 769.093 61.8203C771.053 61.8203 772.603 62.2803 773.743 63.2003C774.903 64.1003 775.483 65.3403 775.483 66.9203C775.483 67.5003 775.433 68.0303 775.333 68.5103C775.253 68.9903 775.083 69.4903 774.823 70.0103C774.563 70.5303 774.173 71.1503 773.653 71.8703C773.153 72.5703 772.493 73.4303 771.673 74.4503C770.853 75.4703 769.833 76.7203 768.613 78.2003L766.423 80.8703H775.063C775.463 80.8703 775.663 81.1703 775.663 81.7703V82.1003C775.663 82.7003 775.463 83.0003 775.063 83.0003H763.603Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="745"
                y="47"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "2" ? "white" : "#818181"}
                stroke-width={pressedKey === "2" ? 1 : 2}
              />
            </g>

            <g id="b">
              <rect
                x="745"
                y="97"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "b" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M764.418 133C763.818 133 763.518 132.7 763.518 132.1V112.9C763.518 112.3 763.818 112 764.418 112H769.788C772.028 112 773.708 112.49 774.828 113.47C775.948 114.45 776.508 115.73 776.508 117.31C776.508 117.97 776.348 118.62 776.028 119.26C775.728 119.9 775.328 120.46 774.828 120.94C774.328 121.4 773.788 121.71 773.208 121.87V121.99C773.208 121.99 773.348 122.04 773.628 122.14C773.908 122.22 774.258 122.38 774.678 122.62C775.098 122.84 775.518 123.14 775.938 123.52C776.358 123.9 776.708 124.37 776.988 124.93C777.268 125.49 777.408 126.17 777.408 126.97C777.408 129.05 776.728 130.58 775.368 131.56C774.028 132.52 772.098 133 769.578 133H764.418ZM766.158 130.72H770.538C771.878 130.72 772.898 130.37 773.598 129.67C774.318 128.97 774.678 128.03 774.678 126.85C774.678 125.75 774.288 124.89 773.508 124.27C772.728 123.65 771.568 123.34 770.028 123.34H766.158V130.72ZM766.158 121.06H769.638C770.858 121.06 771.848 120.72 772.608 120.04C773.388 119.36 773.778 118.53 773.778 117.55C773.778 116.43 773.438 115.61 772.758 115.09C772.098 114.55 771.018 114.28 769.518 114.28H766.158V121.06Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="745"
                y="97"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "b" ? "white" : "#818181"}
                stroke-width={pressedKey === "b" ? 1 : 2}
              />
            </g>

            <g id="slash">
              <rect
                x="745"
                y="169"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "/" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M767.95 198.94C767.63 198.94 767.4 198.88 767.26 198.76C767.12 198.62 767.05 198.45 767.05 198.25C767.05 197.57 767.27 196.81 767.71 195.97C768.17 195.13 768.81 194.3 769.63 193.48C770.39 192.72 771.05 192 771.61 191.32C772.19 190.62 772.48 189.9 772.48 189.16C772.48 188.1 772.16 187.32 771.52 186.82C770.88 186.3 769.94 186.04 768.7 186.04C768.08 186.04 767.51 186.13 766.99 186.31C766.49 186.47 766.07 186.64 765.73 186.82C765.39 186.98 765.14 187.06 764.98 187.06C764.88 187.06 764.75 186.96 764.59 186.76C764.45 186.56 764.32 186.34 764.2 186.1C764.08 185.86 764.02 185.67 764.02 185.53C764.02 185.27 764.18 185.04 764.5 184.84C764.84 184.62 765.27 184.44 765.79 184.3C766.31 184.14 766.87 184.02 767.47 183.94C768.07 183.86 768.64 183.82 769.18 183.82C770.52 183.82 771.64 184.05 772.54 184.51C773.46 184.97 774.15 185.59 774.61 186.37C775.09 187.13 775.33 187.96 775.33 188.86C775.33 189.54 775.19 190.18 774.91 190.78C774.65 191.36 774.3 191.91 773.86 192.43C773.44 192.93 772.98 193.42 772.48 193.9C771.78 194.58 771.12 195.28 770.5 196C769.9 196.7 769.53 197.48 769.39 198.34C769.37 198.54 769.29 198.69 769.15 198.79C769.01 198.89 768.76 198.94 768.4 198.94H767.95ZM767.86 205.06C767.06 205.06 766.66 204.84 766.66 204.4V202.42C766.66 201.98 767.06 201.76 767.86 201.76H768.37C769.17 201.76 769.57 201.98 769.57 202.42V204.4C769.57 204.84 769.17 205.06 768.37 205.06H767.86Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="745"
                y="169"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "/" ? "white" : "#818181"}
                stroke-width={pressedKey === "/" ? 1 : 2}
              />
            </g>

            <g id="arrowup">
              <rect
                x="745"
                y="241"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "arrowup" ? "#ababab" : "#D8D8D8"}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M767.717 262.931C766.951 262.931 766.469 262.104 766.847 261.438L769.63 256.533C770.013 255.858 770.987 255.858 771.37 256.533L774.153 261.438C774.531 262.104 774.049 262.931 773.283 262.931L772.279 262.931C771.727 262.931 771.279 263.379 771.279 263.931L771.279 277.207C771.279 277.645 770.93 278 770.5 278C770.07 278 769.721 277.645 769.721 277.207L769.721 263.931C769.721 263.379 769.273 262.931 768.721 262.931L767.717 262.931Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="745"
                y="241"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "arrowup" ? "white" : "#818181"}
                stroke-width={pressedKey === "arrowup" ? 1 : 2}
              />
            </g>

            <rect x="745" y="291" width="50" height="50" rx="5" fill="#D8D8D8" />
            <rect x="745" y="291" width="50" height="50" rx="5" stroke="#818181" stroke-width="2" />

            <g id="arrowdown">
              <rect
                x="745"
                y="341"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "arrowdown" ? "#ababab" : "#D8D8D8"}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M773.283 370.069C774.049 370.069 774.531 370.896 774.153 371.562L771.37 376.467C770.987 377.142 770.013 377.142 769.63 376.467L766.847 371.562C766.469 370.896 766.951 370.069 767.717 370.069L768.721 370.069C769.273 370.069 769.721 369.621 769.721 369.069L769.721 355.793C769.721 355.355 770.07 355 770.5 355C770.93 355 771.279 355.355 771.279 355.793L771.279 369.069C771.279 369.621 771.727 370.069 772.279 370.069L773.283 370.069Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="745"
                y="341"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "arrowdown" ? "white" : "#818181"}
                stroke-width={pressedKey === "arrowdown" ? 1 : 2}
              />
            </g>

            <g id="4">
              <rect
                x="845"
                y="47"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "4" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M872.753 83.0604C872.153 83.0604 871.853 82.8404 871.853 82.4004V77.9904H862.733C862.633 77.9904 862.523 77.9004 862.403 77.7204C862.283 77.5404 862.183 77.3304 862.103 77.0904C862.043 76.8504 862.013 76.6404 862.013 76.4604C862.013 76.2804 862.033 76.1704 862.073 76.1304L871.943 62.3604C872.043 62.2204 872.183 62.1204 872.363 62.0604C872.563 61.9804 872.763 61.9404 872.963 61.9404H873.443C874.043 61.9404 874.343 62.1604 874.343 62.6004V75.8304H876.353C876.753 75.8304 876.953 76.1304 876.953 76.7304V77.0904C876.953 77.6904 876.753 77.9904 876.353 77.9904H874.343V82.4004C874.343 82.8404 874.043 83.0604 873.443 83.0604H872.753ZM864.983 75.8304H871.853V68.3904C871.853 68.0504 871.863 67.6604 871.883 67.2204C871.903 66.7804 871.943 66.4004 872.003 66.0804L871.883 66.0204L864.983 75.8304Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="845"
                y="47"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "4" ? "white" : "#818181"}
                stroke-width={pressedKey === "4" ? 1 : 2}
              />
            </g>
            <g id="d">
              <rect
                x="845"
                y="97"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "d" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M862.88 133C862.28 133 861.98 132.7 861.98 132.1V112.9C861.98 112.3 862.28 112 862.88 112H868.94C870.66 112 872.22 112.34 873.62 113.02C875.02 113.68 876.13 114.78 876.95 116.32C877.79 117.84 878.21 119.9 878.21 122.5C878.21 124.44 877.97 126.09 877.49 127.45C877.01 128.79 876.35 129.87 875.51 130.69C874.67 131.49 873.69 132.08 872.57 132.46C871.45 132.82 870.24 133 868.94 133H862.88ZM864.62 130.72H868.73C869.99 130.72 871.13 130.45 872.15 129.91C873.17 129.37 873.98 128.5 874.58 127.3C875.18 126.1 875.48 124.5 875.48 122.5C875.48 120.5 875.18 118.9 874.58 117.7C873.98 116.5 873.17 115.63 872.15 115.09C871.13 114.55 869.99 114.28 868.73 114.28H864.62V130.72Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="845"
                y="97"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "d" ? "white" : "#818181"}
                stroke-width={pressedKey === "d" ? 1 : 2}
              />
            </g>
            <g id="5">
              <rect
                x="895"
                y="47"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "5" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M918.493 83.18C917.393 83.18 916.413 83.08 915.553 82.88C914.713 82.68 914.053 82.44 913.573 82.16C913.113 81.88 912.883 81.62 912.883 81.38C912.883 81.18 912.943 80.97 913.063 80.75C913.203 80.51 913.353 80.31 913.513 80.15C913.693 79.97 913.833 79.88 913.933 79.88C914.133 79.88 914.413 79.98 914.773 80.18C915.153 80.36 915.643 80.55 916.243 80.75C916.863 80.93 917.603 81.02 918.463 81.02C919.283 81.02 920.043 80.86 920.743 80.54C921.443 80.22 922.013 79.73 922.453 79.07C922.893 78.41 923.113 77.59 923.113 76.61C923.113 75.27 922.653 74.25 921.733 73.55C920.813 72.85 919.473 72.5 917.713 72.5H915.163C914.843 72.5 914.623 72.43 914.503 72.29C914.383 72.15 914.323 71.92 914.323 71.6L914.833 62.9C914.853 62.58 914.933 62.35 915.073 62.21C915.233 62.07 915.473 62 915.793 62H923.773C924.173 62 924.373 62.3 924.373 62.9V63.26C924.373 63.86 924.173 64.16 923.773 64.16H917.263L916.873 70.34H918.433C919.253 70.34 920.083 70.44 920.923 70.64C921.763 70.84 922.543 71.18 923.263 71.66C924.003 72.12 924.593 72.76 925.033 73.58C925.473 74.38 925.693 75.39 925.693 76.61C925.693 77.91 925.483 78.99 925.063 79.85C924.643 80.69 924.073 81.36 923.353 81.86C922.653 82.34 921.873 82.68 921.013 82.88C920.173 83.08 919.333 83.18 918.493 83.18Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="895"
                y="47"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "5" ? "white" : "#818181"}
                stroke-width={pressedKey === "5" ? 1 : 2}
              />
            </g>

            <g id="e">
              <rect
                x="895"
                y="97"
                width="50"
                height="50"
                rx="5"
                fill={pressedKey === "e" ? "#ababab" : "#D8D8D8"}
              />
              <path
                d="M915.151 133C914.551 133 914.251 132.7 914.251 132.1V112.9C914.251 112.3 914.551 112 915.151 112H925.621C926.021 112 926.221 112.3 926.221 112.9V113.38C926.221 113.98 926.021 114.28 925.621 114.28H916.891V121.12H924.721C925.121 121.12 925.321 121.42 925.321 122.02V122.5C925.321 123.1 925.121 123.4 924.721 123.4H916.891V130.72H925.651C926.051 130.72 926.251 131.02 926.251 131.62V132.1C926.251 132.7 926.051 133 925.651 133H915.151Z"
                fill="black"
                fill-opacity="0.83"
              />
              <rect
                x="895"
                y="97"
                width="50"
                height="50"
                rx="5"
                stroke={pressedKey === "e" ? "white" : "#818181"}
                stroke-width={pressedKey === "e" ? 1 : 2}
              />
            </g>

            <g id="correctie">
              <circle
                cx="44"
                cy="44"
                r="45"
                transform="matrix(-1 0 0 1 1093 183)"
                fill={pressedKey === "backspace" ? "#ababab" : "white"}
                stroke={pressedKey === "backspace" ? "white" : "#818181"}
                stroke-width="2"
              />
              <g id="correctie_text">
                <path
                  d="M1027.32 231.036C1026.22 231.036 1025.41 230.749 1024.89 230.176C1024.36 229.596 1024.1 228.696 1024.1 227.476C1024.1 225.103 1025.18 223.916 1027.34 223.916C1027.56 223.916 1027.78 223.933 1028 223.966C1028.21 223.999 1028.4 224.043 1028.58 224.096C1028.75 224.149 1028.89 224.209 1029.01 224.276C1029.17 224.369 1029.25 224.456 1029.25 224.536C1029.25 224.589 1029.23 224.663 1029.19 224.756C1029.15 224.849 1029.1 224.933 1029.05 225.006C1029 225.079 1028.95 225.116 1028.91 225.116C1028.87 225.116 1028.79 225.079 1028.67 225.006C1028.55 224.933 1028.38 224.859 1028.16 224.786C1027.94 224.713 1027.68 224.676 1027.36 224.676C1026.56 224.676 1025.96 224.899 1025.58 225.346C1025.2 225.786 1025.01 226.499 1025.01 227.486C1025.01 228.446 1025.19 229.153 1025.57 229.606C1025.95 230.053 1026.53 230.276 1027.32 230.276C1027.66 230.276 1027.95 230.236 1028.19 230.156C1028.43 230.069 1028.62 229.986 1028.76 229.906C1028.9 229.819 1029 229.776 1029.04 229.776C1029.08 229.776 1029.13 229.813 1029.19 229.886C1029.24 229.959 1029.29 230.039 1029.33 230.126C1029.37 230.213 1029.39 230.276 1029.39 230.316C1029.39 230.363 1029.36 230.416 1029.3 230.476C1029.24 230.536 1029.17 230.593 1029.07 230.646C1028.95 230.713 1028.8 230.776 1028.62 230.836C1028.44 230.896 1028.25 230.943 1028.03 230.976C1027.81 231.016 1027.57 231.036 1027.32 231.036Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1033.17 231.036C1032.17 231.036 1031.41 230.733 1030.88 230.126C1030.36 229.513 1030.09 228.623 1030.09 227.456C1030.09 226.283 1030.36 225.399 1030.88 224.806C1031.41 224.213 1032.17 223.916 1033.17 223.916C1034.18 223.916 1034.95 224.213 1035.47 224.806C1036 225.399 1036.26 226.283 1036.26 227.456C1036.26 228.623 1036 229.513 1035.47 230.126C1034.95 230.733 1034.18 231.036 1033.17 231.036ZM1033.17 230.276C1033.88 230.276 1034.42 230.036 1034.79 229.556C1035.17 229.076 1035.35 228.376 1035.35 227.456C1035.35 226.536 1035.17 225.843 1034.79 225.376C1034.42 224.909 1033.88 224.676 1033.17 224.676C1032.47 224.676 1031.93 224.909 1031.55 225.376C1031.19 225.843 1031 226.536 1031 227.456C1031 228.376 1031.19 229.076 1031.55 229.556C1031.93 230.036 1032.47 230.276 1033.17 230.276Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1038.21 230.996C1037.94 230.996 1037.81 230.923 1037.81 230.776V224.276C1037.81 224.076 1037.91 223.976 1038.11 223.976H1039.79C1040.57 223.976 1041.16 224.113 1041.58 224.386C1041.99 224.659 1042.2 225.113 1042.2 225.746C1042.2 226.173 1042.09 226.526 1041.89 226.806C1041.68 227.086 1041.4 227.296 1041.05 227.436V227.476C1041.26 227.543 1041.43 227.686 1041.56 227.906C1041.69 228.119 1041.82 228.393 1041.95 228.726L1042.73 230.696C1042.75 230.756 1042.76 230.803 1042.76 230.836C1042.76 230.943 1042.62 230.996 1042.34 230.996H1042.24C1042.14 230.996 1042.05 230.983 1041.98 230.956C1041.9 230.923 1041.86 230.883 1041.84 230.836L1041.08 228.876C1040.93 228.483 1040.74 228.223 1040.52 228.096C1040.29 227.963 1039.98 227.896 1039.59 227.896H1038.69V230.776C1038.69 230.923 1038.55 230.996 1038.29 230.996H1038.21ZM1038.69 227.136H1040.01C1040.25 227.136 1040.46 227.079 1040.66 226.966C1040.85 226.853 1041 226.703 1041.12 226.516C1041.23 226.329 1041.29 226.126 1041.29 225.906C1041.29 225.499 1041.16 225.203 1040.92 225.016C1040.67 224.829 1040.34 224.736 1039.94 224.736H1038.69V227.136Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1044.31 230.996C1044.04 230.996 1043.91 230.923 1043.91 230.776V224.276C1043.91 224.076 1044.01 223.976 1044.21 223.976H1045.89C1046.67 223.976 1047.27 224.113 1047.68 224.386C1048.09 224.659 1048.3 225.113 1048.3 225.746C1048.3 226.173 1048.2 226.526 1047.99 226.806C1047.78 227.086 1047.5 227.296 1047.15 227.436V227.476C1047.36 227.543 1047.53 227.686 1047.66 227.906C1047.79 228.119 1047.92 228.393 1048.05 228.726L1048.83 230.696C1048.85 230.756 1048.86 230.803 1048.86 230.836C1048.86 230.943 1048.72 230.996 1048.44 230.996H1048.34C1048.24 230.996 1048.15 230.983 1048.08 230.956C1048.01 230.923 1047.96 230.883 1047.94 230.836L1047.18 228.876C1047.03 228.483 1046.85 228.223 1046.62 228.096C1046.39 227.963 1046.08 227.896 1045.69 227.896H1044.79V230.776C1044.79 230.923 1044.66 230.996 1044.39 230.996H1044.31ZM1044.79 227.136H1046.11C1046.35 227.136 1046.57 227.079 1046.76 226.966C1046.95 226.853 1047.11 226.703 1047.22 226.516C1047.33 226.329 1047.39 226.126 1047.39 225.906C1047.39 225.499 1047.27 225.203 1047.02 225.016C1046.77 224.829 1046.45 224.736 1046.04 224.736H1044.79V227.136Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1050.32 230.976C1050.12 230.976 1050.02 230.876 1050.02 230.676V224.276C1050.02 224.076 1050.12 223.976 1050.32 223.976H1053.81C1053.95 223.976 1054.01 224.076 1054.01 224.276V224.436C1054.01 224.636 1053.95 224.736 1053.81 224.736H1050.9V227.016H1053.51C1053.65 227.016 1053.71 227.116 1053.71 227.316V227.476C1053.71 227.676 1053.65 227.776 1053.51 227.776H1050.9V230.216H1053.82C1053.96 230.216 1054.02 230.316 1054.02 230.516V230.676C1054.02 230.876 1053.96 230.976 1053.82 230.976H1050.32Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1058.29 231.036C1057.2 231.036 1056.39 230.749 1055.86 230.176C1055.34 229.596 1055.07 228.696 1055.07 227.476C1055.07 225.103 1056.15 223.916 1058.31 223.916C1058.54 223.916 1058.76 223.933 1058.97 223.966C1059.19 223.999 1059.38 224.043 1059.55 224.096C1059.73 224.149 1059.87 224.209 1059.98 224.276C1060.14 224.369 1060.22 224.456 1060.22 224.536C1060.22 224.589 1060.2 224.663 1060.16 224.756C1060.12 224.849 1060.08 224.933 1060.02 225.006C1059.98 225.079 1059.93 225.116 1059.88 225.116C1059.85 225.116 1059.77 225.079 1059.64 225.006C1059.52 224.933 1059.35 224.859 1059.13 224.786C1058.92 224.713 1058.65 224.676 1058.33 224.676C1057.53 224.676 1056.94 224.899 1056.55 225.346C1056.17 225.786 1055.98 226.499 1055.98 227.486C1055.98 228.446 1056.17 229.153 1056.54 229.606C1056.92 230.053 1057.51 230.276 1058.29 230.276C1058.64 230.276 1058.93 230.236 1059.16 230.156C1059.4 230.069 1059.59 229.986 1059.73 229.906C1059.88 229.819 1059.97 229.776 1060.01 229.776C1060.06 229.776 1060.11 229.813 1060.16 229.886C1060.22 229.959 1060.26 230.039 1060.3 230.126C1060.34 230.213 1060.36 230.276 1060.36 230.316C1060.36 230.363 1060.33 230.416 1060.27 230.476C1060.22 230.536 1060.14 230.593 1060.04 230.646C1059.92 230.713 1059.77 230.776 1059.59 230.836C1059.42 230.896 1059.22 230.943 1059 230.976C1058.78 231.016 1058.55 231.036 1058.29 231.036Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1063.41 230.996C1063.15 230.996 1063.01 230.923 1063.01 230.776V224.736H1061.09C1060.96 224.736 1060.89 224.636 1060.89 224.436V224.276C1060.89 224.076 1060.96 223.976 1061.09 223.976H1065.78C1065.92 223.976 1065.98 224.076 1065.98 224.276V224.436C1065.98 224.636 1065.92 224.736 1065.78 224.736H1063.89V230.776C1063.89 230.923 1063.76 230.996 1063.49 230.996H1063.41Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1067.55 230.996C1067.29 230.996 1067.15 230.923 1067.15 230.776V224.176C1067.15 224.029 1067.29 223.956 1067.55 223.956H1067.63C1067.9 223.956 1068.03 224.029 1068.03 224.176V230.776C1068.03 230.923 1067.9 230.996 1067.63 230.996H1067.55Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1070.29 230.976C1070.09 230.976 1069.99 230.876 1069.99 230.676V224.276C1069.99 224.076 1070.09 223.976 1070.29 223.976H1073.78C1073.92 223.976 1073.98 224.076 1073.98 224.276V224.436C1073.98 224.636 1073.92 224.736 1073.78 224.736H1070.87V227.016H1073.48C1073.62 227.016 1073.68 227.116 1073.68 227.316V227.476C1073.68 227.676 1073.62 227.776 1073.48 227.776H1070.87V230.216H1073.79C1073.93 230.216 1073.99 230.316 1073.99 230.516V230.676C1073.99 230.876 1073.93 230.976 1073.79 230.976H1070.29Z"
                  fill="black"
                  fill-opacity="0.61"
                />
              </g>
            </g>

            <g id="doorgaan">
              <circle
                cx="44"
                cy="44"
                r="45"
                transform="matrix(-1 0 0 1 1093 302)"
                fill={pressedKey === "enter" ? "#ababab" : "white"}
                stroke={pressedKey === "enter" ? "white" : "#818181"}
                stroke-width="2"
              />
              <g id="doorgaan_text">
                <path
                  d="M1023.39 349.976C1023.19 349.976 1023.09 349.876 1023.09 349.676V343.276C1023.09 343.076 1023.19 342.976 1023.39 342.976H1025.41C1025.98 342.976 1026.5 343.089 1026.97 343.316C1027.44 343.536 1027.81 343.903 1028.08 344.416C1028.36 344.923 1028.5 345.609 1028.5 346.476C1028.5 347.123 1028.42 347.673 1028.26 348.126C1028.1 348.573 1027.88 348.933 1027.6 349.206C1027.32 349.473 1026.99 349.669 1026.62 349.796C1026.25 349.916 1025.84 349.976 1025.41 349.976H1023.39ZM1023.97 349.216H1025.34C1025.76 349.216 1026.14 349.126 1026.48 348.946C1026.82 348.766 1027.09 348.476 1027.29 348.076C1027.49 347.676 1027.59 347.143 1027.59 346.476C1027.59 345.809 1027.49 345.276 1027.29 344.876C1027.09 344.476 1026.82 344.186 1026.48 344.006C1026.14 343.826 1025.76 343.736 1025.34 343.736H1023.97V349.216Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1032.72 350.036C1031.72 350.036 1030.96 349.733 1030.43 349.126C1029.91 348.513 1029.64 347.623 1029.64 346.456C1029.64 345.283 1029.91 344.399 1030.43 343.806C1030.96 343.213 1031.72 342.916 1032.72 342.916C1033.73 342.916 1034.5 343.213 1035.02 343.806C1035.55 344.399 1035.81 345.283 1035.81 346.456C1035.81 347.623 1035.55 348.513 1035.02 349.126C1034.5 349.733 1033.73 350.036 1032.72 350.036ZM1032.72 349.276C1033.43 349.276 1033.97 349.036 1034.34 348.556C1034.72 348.076 1034.9 347.376 1034.9 346.456C1034.9 345.536 1034.72 344.843 1034.34 344.376C1033.97 343.909 1033.43 343.676 1032.72 343.676C1032.02 343.676 1031.48 343.909 1031.1 344.376C1030.74 344.843 1030.55 345.536 1030.55 346.456C1030.55 347.376 1030.74 348.076 1031.1 348.556C1031.48 349.036 1032.02 349.276 1032.72 349.276Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1040.04 350.036C1039.04 350.036 1038.27 349.733 1037.75 349.126C1037.22 348.513 1036.96 347.623 1036.96 346.456C1036.96 345.283 1037.22 344.399 1037.75 343.806C1038.27 343.213 1039.04 342.916 1040.04 342.916C1041.04 342.916 1041.81 343.213 1042.34 343.806C1042.86 344.399 1043.13 345.283 1043.13 346.456C1043.13 347.623 1042.86 348.513 1042.34 349.126C1041.81 349.733 1041.04 350.036 1040.04 350.036ZM1040.04 349.276C1040.74 349.276 1041.28 349.036 1041.66 348.556C1042.03 348.076 1042.22 347.376 1042.22 346.456C1042.22 345.536 1042.03 344.843 1041.66 344.376C1041.28 343.909 1040.74 343.676 1040.04 343.676C1039.33 343.676 1038.79 343.909 1038.42 344.376C1038.05 344.843 1037.87 345.536 1037.87 346.456C1037.87 347.376 1038.05 348.076 1038.42 348.556C1038.79 349.036 1039.33 349.276 1040.04 349.276Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1045.07 349.996C1044.81 349.996 1044.67 349.923 1044.67 349.776V343.276C1044.67 343.076 1044.77 342.976 1044.97 342.976H1046.65C1047.43 342.976 1048.03 343.113 1048.44 343.386C1048.86 343.659 1049.06 344.113 1049.06 344.746C1049.06 345.173 1048.96 345.526 1048.75 345.806C1048.55 346.086 1048.27 346.296 1047.91 346.436V346.476C1048.13 346.543 1048.3 346.686 1048.42 346.906C1048.56 347.119 1048.69 347.393 1048.81 347.726L1049.59 349.696C1049.61 349.756 1049.62 349.803 1049.62 349.836C1049.62 349.943 1049.48 349.996 1049.2 349.996H1049.1C1049 349.996 1048.92 349.983 1048.84 349.956C1048.77 349.923 1048.72 349.883 1048.7 349.836L1047.94 347.876C1047.8 347.483 1047.61 347.223 1047.38 347.096C1047.16 346.963 1046.85 346.896 1046.45 346.896H1045.55V349.776C1045.55 349.923 1045.42 349.996 1045.15 349.996H1045.07ZM1045.55 346.136H1046.87C1047.11 346.136 1047.33 346.079 1047.52 345.966C1047.72 345.853 1047.87 345.703 1047.98 345.516C1048.1 345.329 1048.15 345.126 1048.15 344.906C1048.15 344.499 1048.03 344.203 1047.78 344.016C1047.54 343.829 1047.21 343.736 1046.8 343.736H1045.55V346.136Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1053.37 350.036C1052.37 350.036 1051.61 349.749 1051.08 349.176C1050.54 348.603 1050.28 347.723 1050.28 346.536C1050.28 345.289 1050.55 344.376 1051.11 343.796C1051.67 343.209 1052.47 342.916 1053.52 342.916C1053.76 342.916 1054 342.936 1054.23 342.976C1054.46 343.016 1054.67 343.066 1054.85 343.126C1055.03 343.186 1055.17 343.253 1055.27 343.326C1055.37 343.399 1055.43 343.469 1055.43 343.536C1055.43 343.589 1055.41 343.663 1055.37 343.756C1055.33 343.849 1055.28 343.933 1055.23 344.006C1055.18 344.079 1055.13 344.116 1055.09 344.116C1055.05 344.116 1054.97 344.079 1054.85 344.006C1054.73 343.933 1054.56 343.859 1054.34 343.786C1054.12 343.713 1053.86 343.676 1053.54 343.676C1052.95 343.676 1052.48 343.786 1052.14 344.006C1051.8 344.226 1051.55 344.543 1051.41 344.956C1051.26 345.369 1051.19 345.866 1051.19 346.446C1051.19 346.826 1051.22 347.189 1051.29 347.536C1051.35 347.876 1051.47 348.176 1051.64 348.436C1051.8 348.696 1052.03 348.903 1052.32 349.056C1052.61 349.203 1052.98 349.276 1053.43 349.276C1053.73 349.276 1054.02 349.259 1054.28 349.226C1054.54 349.186 1054.73 349.146 1054.84 349.106L1054.72 349.486V347.126H1053.55C1053.41 347.126 1053.35 347.026 1053.35 346.826V346.666C1053.35 346.466 1053.41 346.366 1053.55 346.366H1055.3C1055.5 346.366 1055.6 346.466 1055.6 346.666V349.316C1055.6 349.456 1055.53 349.573 1055.41 349.666C1055.29 349.759 1055.12 349.833 1054.91 349.886C1054.7 349.939 1054.46 349.976 1054.2 349.996C1053.93 350.023 1053.65 350.036 1053.37 350.036Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1056.98 349.996C1056.69 349.996 1056.55 349.939 1056.55 349.826C1056.55 349.806 1056.55 349.786 1056.56 349.766C1056.56 349.739 1056.57 349.713 1056.58 349.686L1058.95 343.116C1058.98 343.009 1059.11 342.956 1059.35 342.956H1059.64C1059.87 342.956 1060 343.009 1060.04 343.116L1062.41 349.696C1062.43 349.743 1062.44 349.786 1062.44 349.826C1062.44 349.939 1062.29 349.996 1062.01 349.996H1061.93C1061.83 349.996 1061.74 349.986 1061.67 349.966C1061.59 349.939 1061.55 349.896 1061.53 349.836L1060.94 348.196H1058L1057.43 349.836C1057.41 349.896 1057.36 349.939 1057.29 349.966C1057.21 349.986 1057.13 349.996 1057.03 349.996H1056.98ZM1058.28 347.436H1060.67L1059.62 344.456C1059.59 344.363 1059.57 344.266 1059.55 344.166C1059.53 344.066 1059.51 343.969 1059.5 343.876H1059.48C1059.46 343.969 1059.43 344.066 1059.41 344.166C1059.39 344.266 1059.36 344.363 1059.33 344.456L1058.28 347.436Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1063.11 349.996C1062.82 349.996 1062.68 349.939 1062.68 349.826C1062.68 349.806 1062.68 349.786 1062.69 349.766C1062.7 349.739 1062.7 349.713 1062.71 349.686L1065.08 343.116C1065.11 343.009 1065.25 342.956 1065.48 342.956H1065.77C1066 342.956 1066.14 343.009 1066.17 343.116L1068.54 349.696C1068.56 349.743 1068.57 349.786 1068.57 349.826C1068.57 349.939 1068.43 349.996 1068.14 349.996H1068.06C1067.96 349.996 1067.87 349.986 1067.8 349.966C1067.73 349.939 1067.68 349.896 1067.66 349.836L1067.07 348.196H1064.13L1063.56 349.836C1063.54 349.896 1063.49 349.939 1063.42 349.966C1063.35 349.986 1063.26 349.996 1063.16 349.996H1063.11ZM1064.41 347.436H1066.8L1065.75 344.456C1065.72 344.363 1065.7 344.266 1065.68 344.166C1065.66 344.066 1065.64 343.969 1065.63 343.876H1065.61C1065.59 343.969 1065.57 344.066 1065.54 344.166C1065.52 344.266 1065.49 344.363 1065.46 344.456L1064.41 347.436Z"
                  fill="black"
                  fill-opacity="0.61"
                />
                <path
                  d="M1069.99 349.996C1069.73 349.996 1069.59 349.923 1069.59 349.776V343.176C1069.59 343.029 1069.73 342.956 1069.99 342.956H1070.2C1070.32 342.956 1070.4 342.969 1070.46 342.996C1070.52 343.023 1070.58 343.073 1070.62 343.146L1073.76 347.886C1073.83 347.986 1073.89 348.086 1073.94 348.186C1074 348.279 1074.05 348.379 1074.09 348.486L1074.13 348.476C1074.13 348.363 1074.12 348.259 1074.11 348.166C1074.11 348.066 1074.11 347.943 1074.11 347.796V343.176C1074.11 343.029 1074.25 342.956 1074.51 342.956H1074.58C1074.85 342.956 1074.98 343.029 1074.98 343.176V349.776C1074.98 349.923 1074.85 349.996 1074.58 349.996H1074.38C1074.29 349.996 1074.22 349.986 1074.16 349.966C1074.11 349.939 1074.07 349.903 1074.03 349.856L1070.81 344.996C1070.75 344.896 1070.69 344.799 1070.63 344.706C1070.59 344.606 1070.54 344.503 1070.48 344.396L1070.44 344.406C1070.46 344.519 1070.46 344.626 1070.46 344.726C1070.46 344.819 1070.46 344.939 1070.46 345.086V349.776C1070.46 349.923 1070.33 349.996 1070.06 349.996H1069.99Z"
                  fill="black"
                  fill-opacity="0.61"
                />
              </g>
            </g>

            <g id="tone_buttons">
              <g id="low_tone">
                <circle
                  cx="53"
                  cy="53"
                  r="54"
                  transform="matrix(-1 0 0 1 159 284)"
                  fill={pressedKey === "f6" ? "black" : "#262626"}
                  stroke={pressedKey === "f6" ? "white" : "#818181"}
                  stroke-width="2"
                />
                <path
                  d="M100.932 353.312C100.052 353.312 99.6118 352.872 99.6118 351.992V323.392C99.6118 322.746 100.198 322.424 101.372 322.424H101.724C102.897 322.424 103.484 322.746 103.484 323.392V349.968H115.584C116.17 349.968 116.464 350.408 116.464 351.288V351.992C116.464 352.872 116.17 353.312 115.584 353.312H100.932Z"
                  fill="white"
                />
              </g>
              <g id="high_tone">
                <circle
                  cx="53"
                  cy="53"
                  r="54"
                  transform="matrix(-1 0 0 1 590 284)"
                  fill={pressedKey === "f7" ? "black" : "#262626"}
                  stroke={pressedKey === "f7" ? "white" : "#818181"}
                  stroke-width="2"
                />
                <path
                  d="M526.937 353.4C525.764 353.4 525.177 353.077 525.177 352.432V323.392C525.177 322.746 525.764 322.424 526.937 322.424H527.289C528.463 322.424 529.049 322.746 529.049 323.392V335.844H544.977V323.392C544.977 322.746 545.564 322.424 546.737 322.424H547.089C548.263 322.424 548.849 322.746 548.849 323.392V352.432C548.849 353.077 548.263 353.4 547.089 353.4H546.737C545.564 353.4 544.977 353.077 544.977 352.432V339.188H529.049V352.432C529.049 353.077 528.463 353.4 527.289 353.4H526.937Z"
                  fill="white"
                />
              </g>
            </g>

            <g id="color_buttons">
              <circle
                id="white_button"
                cx="53"
                cy="53"
                r="54"
                transform="matrix(-1 0 0 1 187 79)"
                fill={pressedKey === "f1" ? "#ABABAB" : "white"}
                stroke={pressedKey === "f1" ? "white" : "#818181"}
                stroke-width="2"
              />
              <circle
                id="blue_button"
                cx="53"
                cy="53"
                r="54"
                transform="matrix(-1 0 0 1 371 79)"
                fill={pressedKey === "f2" ? "#002366" : "#085CFF"}
                stroke={pressedKey === "f2" ? "white" : "#818181"}
                stroke-width="2"
              />
              <circle
                id="yellow_button"
                cx="53"
                cy="53"
                r="54"
                transform="matrix(-1 0 0 1 555 79)"
                fill={pressedKey === "f3" ? "#D6A700" : "#FFD12F"}
                stroke={pressedKey === "f3" ? "white" : "#818181"}
                stroke-width="2"
              />
              <circle
                id="red_button"
                cx="53"
                cy="53"
                r="54"
                transform="matrix(-1 0 0 1 281 206)"
                fill={pressedKey === "f4" ? "#AA0D00" : "#FF3F2F"}
                stroke={pressedKey === "f4" ? "white" : "#818181"}
                stroke-width="2"
              />
              <circle
                id="green_button"
                cx="53"
                cy="53"
                r="54"
                transform="matrix(-1 0 0 1 465 206)"
                fill={pressedKey === "f5" ? "#1C7100" : "#37DF00"}
                stroke={pressedKey === "f5" ? "white" : "#818181"}
                stroke-width="2"
              />
            </g>
          </g>
          <defs>
            <clipPath id="clip0_2540_19046">
              <rect width="1150" height="438" rx="10" fill="white" />
            </clipPath>
          </defs>
        </svg>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <svg
            width="85"
            height="191"
            viewBox="0 0 85 191"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="0.5"
              y="0.5"
              width="84"
              height="190"
              rx="4.5"
              fill={pressedKey === leftPedal ? "#A8A8A8" : "#D8D8D8"}
            />
            <rect
              x="11"
              y="11"
              width="65"
              height="18"
              fill={pressedKey === leftPedal ? "#747474" : "#B9B9B9"}
            />
            <rect
              x="11"
              y="39"
              width="65"
              height="18"
              fill={pressedKey === leftPedal ? "#747474" : "#B9B9B9"}
            />
            <rect
              x="11"
              y="67"
              width="65"
              height="18"
              fill={pressedKey === leftPedal ? "#747474" : "#B9B9B9"}
            />
            <rect
              x="0.5"
              y="0.5"
              width="84"
              height="190"
              rx="4.5"
              stroke={pressedKey === leftPedal ? "white" : "#818181"}
            />
          </svg>

          <svg
            width="85"
            height="191"
            viewBox="0 0 85 191"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="0.5"
              y="0.5"
              width="84"
              height="190"
              rx="4.5"
              fill={pressedKey === rightPedal ? "#A8A8A8" : "#D8D8D8"}
            />
            <rect
              x="11"
              y="11"
              width="65"
              height="18"
              fill={pressedKey === rightPedal ? "#747474" : "#B9B9B9"}
            />
            <rect
              x="11"
              y="39"
              width="65"
              height="18"
              fill={pressedKey === rightPedal ? "#747474" : "#B9B9B9"}
            />
            <rect
              x="11"
              y="67"
              width="65"
              height="18"
              fill={pressedKey === rightPedal ? "#747474" : "#B9B9B9"}
            />
            <rect
              x="0.5"
              y="0.5"
              width="84"
              height="190"
              rx="4.5"
              stroke={pressedKey === rightPedal ? "white" : "#818181"}
            />
          </svg>
        </div>

        <Box sx={{ width: "100%", p: 2, display: "grid", placeItems: "center" }}>
          <HCButton state={{ kind: "primary", maxWidth: "228px" }} onClick={() => endFn()}>
            <Trans id="introduction.modal.closebutton">Introductie afsluiten</Trans>
          </HCButton>
        </Box>
      </div>
    </>
  );
}
